import { z, type ZodType } from 'zod';
import { AccessPolicyConsolePayloadSchema } from '@/schemas/admin/accessPolicyConsole';
import { UserAccessWorkbookPayloadSchema } from '@/schemas/admin/userAccessWorkbook';
import { ApprovalContextPayloadSchema } from '@/schemas/approvals/approval';
import { BankAccountManagerPayloadSchema } from '@/schemas/bankAccounts/bankAccount';
import { CashBalancesPayloadSchema } from '@/schemas/cash/cashBalance';
import { CloseoutBatchesPayloadSchema } from '@/schemas/closeout/closeout';
import { BatchDealCapturePayloadSchema } from '@/schemas/deals/batch';
import { BondDealWorkbookPayloadSchema } from '@/schemas/deals/bondDealWorkbook';
import {
  DealDetailPayloadSchema,
  DealListPayloadSchema,
} from '@/schemas/deals/deal';
import { EodOperatorPayloadSchema } from '@/schemas/eod/eod';
import {
  CreateInstructionPayloadSchema,
  InstructionDetailPayloadSchema,
} from '@/schemas/instructions/instruction';
import {
  JournalDetailPayloadSchema,
  JournalListPayloadSchema,
  PeriodClosePayloadSchema,
  PostSettlementEntryPayloadSchema,
} from '@/schemas/journals/journal';
import { FixingsQueryFiltersSchema, FixingSchema } from '@/schemas/marketdata/fixing';
import { ExecuteNettingPayloadSchema, NettingSetsPayloadSchema } from '@/schemas/netting/netting';
import { GeneratePaymentPayloadSchema, PaymentDetailPayloadSchema } from '@/schemas/payments/payment';
import { PartyHierarchyPayloadSchema } from '@/schemas/parties/party';
import {
  PortfolioPositionsPayloadSchema,
  PortfolioSummaryPayloadSchema,
} from '@/schemas/portfolio/position';
import { UserPreferencesSchema } from '@/schemas/preferences/userPreferences';
import { BondPretradeTicketPayloadSchema } from '@/schemas/pretrade/bondPretradeTicket';
import { MatchedBookPayloadSchema } from '@/schemas/reports/matchedBook';
import { RatesDeskPayloadSchema } from '@/schemas/reports/ratesDesk';
import { InstructSettlementPayloadSchema, SettlementListPayloadSchema } from '@/schemas/settlements/settlement';
import { CreateSimulationFormSchema, RunSimulationFormSchema } from '@/schemas/simulations/simulation';
import { FixCashflowFormSchema } from '@/schemas/cashflows/fixCashflow';
import { RunValuationFormSchema } from '@/schemas/valuations/valuation';
import {
  InstrumentDetailPayloadSchema,
  InstrumentEventsPayloadSchema,
  InstrumentManagerPayloadSchema,
} from '@/schemas/products/instruments';

const payloadSchemaMap: Record<string, ZodType<unknown>> = {
  preferences: UserPreferencesSchema.partial(),
  bond_deal_workbook: BondDealWorkbookPayloadSchema,
  bond_pretrade_ticket: BondPretradeTicketPayloadSchema.partial(),
  batch_deal_capture: BatchDealCapturePayloadSchema,
  approval_context: ApprovalContextPayloadSchema,
  portfolio_positions: PortfolioPositionsPayloadSchema,
  portfolio_summary: PortfolioSummaryPayloadSchema,
  deal_list: DealListPayloadSchema,
  deal_detail: DealDetailPayloadSchema,
  capture_fixing: FixingSchema.partial(),
  fixings_query: FixingsQueryFiltersSchema,
  run_valuation: RunValuationFormSchema.partial(),
  create_simulation: CreateSimulationFormSchema.partial(),
  run_simulation: RunSimulationFormSchema.partial(),
  fix_cashflow: FixCashflowFormSchema.partial(),
  settlement_list: SettlementListPayloadSchema,
  instruct_settlement: InstructSettlementPayloadSchema,
  generate_payment: GeneratePaymentPayloadSchema,
  payment_detail: PaymentDetailPayloadSchema,
  execute_netting: ExecuteNettingPayloadSchema,
  netting_sets: NettingSetsPayloadSchema,
  party_hierarchy: PartyHierarchyPayloadSchema,
  bank_account_manager: BankAccountManagerPayloadSchema,
  eod_operator: EodOperatorPayloadSchema,
  journal_list: JournalListPayloadSchema,
  journal_detail: JournalDetailPayloadSchema,
  post_settlement_entry: PostSettlementEntryPayloadSchema,
  period_close: PeriodClosePayloadSchema,
  create_instruction: CreateInstructionPayloadSchema,
  instruction_detail: InstructionDetailPayloadSchema,
  cash_balances: CashBalancesPayloadSchema,
  closeout_batches: CloseoutBatchesPayloadSchema,
  matched_book: MatchedBookPayloadSchema,
  rates_desk: RatesDeskPayloadSchema,
  instrument_detail: InstrumentDetailPayloadSchema,
  instrument_events: InstrumentEventsPayloadSchema,
  instrument_manager: InstrumentManagerPayloadSchema,
  user_access_workbook: UserAccessWorkbookPayloadSchema,
  access_policy_console: AccessPolicyConsolePayloadSchema,
};

export function validatePayloadWithSchemaMap(surface: string, payload: unknown): boolean {
  const schema = payloadSchemaMap[surface];
  if (!schema) {
    return true;
  }
  return schema.safeParse(payload).success;
}

export function hasPayloadSchema(surface: string): boolean {
  return payloadSchemaMap[surface] != null;
}

export function payloadSchemaFor(surface: string): ZodType<unknown> {
  return payloadSchemaMap[surface] ?? z.unknown();
}
